<script lang="ts">
import "@cisco-u/link/cu-link.js";
import "@cisco-u/checkbox/cu-checkbox.js";
import "@cisco-u/radio-buttons/cu-radio-buttons.js";
import "@cisco-u/icons/icons/cu-icon-close-x.js";
import "@cisco-u/icons/icons/cu-icon-check.js";
import "@cisco-u/icons/icons/cu-icon-eye-closed.js";
import "@cisco-u/icons/icons/cu-icon-line-diagonal.js";
import "@cisco-u/icons/icons/cu-icon-delete.js";
import "@cisco-u/modal/cu-modal.js";

import { defineComponent } from "vue";
import { mapActions } from "vuex";

import GenAiModelHeader from "@/layout/GenAIBeta/assets/genAiModelHeader.vue";
import GenAiPagination from "@/layout/GenAIBeta/assets/genAIPagination.vue";
import { enrollGenAIUser } from "@/services/profileService/genAI";

export default defineComponent({
  emits: ["hideGenAIModel"],
  components: { GenAiPagination, GenAiModelHeader },
  props: {
    showModel: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      current_slide_number: 1,
      hasClickedAgree: false,
      faqLink:
        "https://cisco-learning-support.atlassian.net/wiki/external/NWMxNDMzOGZiZDE5NDM1YWEwM2FmMjFkMDM5N2ZhNGQ",
    };
  },
  computed: {
    isShowModel(): boolean {
      return this.showModel;
    },
  },
  methods: {
    ...mapActions({
      fetchStatus: "user/fetchStatus",
    }),
    closeModal() {
      this.$emit("hideGenAIModel");
    },
    nextSlide() {
      this.current_slide_number++;
    },
    prevSlide() {
      this.current_slide_number--;
    },
    clickPPA() {
      this.hasClickedAgree = true;
      globalThis.open(
        "https://www.cisco.com/site/us/en/learn/training-certifications/training/learn-ai-assistant.html ",
        "_blank",
        "noopener"
      );
    },
    handleOptIn() {
      const postObj = {
        ai_opt_in: true,
      };
      this.handleGenAIUser(postObj);
    },
    handleGenAIUser(postObj: any) {
      enrollGenAIUser(postObj)
        .then((data) => {
          if (data) {
            this.fetchStatus();
            globalThis.location.href = "/ai-assistant";
            this.closeModal();
          }
        })
        .catch(() => {});
    },

    showBgImage() {
      return this.current_slide_number === 1;
    },
  },
});
</script>

<template>
  <cu-modal
    v-if="isShowModel"
    class="enroll-genai-modal"
    type="large"
    width="48rem"
    :open="isShowModel"
    data-cy="enroll-genai-modal"
    @modalResponse="closeModal"
  >
    <div slot="body" class="enroll-genai-body">
      <div>
        <GenAiModelHeader
          class="max-w-[48rem] xsmobile:h-[20.188rem]"
          :currentSlideNumber="current_slide_number"
          :showBgImage="showBgImage()"
          @close-genai-model="closeModal()"
        >
        </GenAiModelHeader>
        <div
          class="modal-container relative mx-auto my-0 rounded-b-lg border border-t-0 border-black-light bg-white-lightest dark:bg-black max-w-[48rem]"
        >
          <div class="modal-body text-black-dark">
            <slot name="body">
              <div v-if="current_slide_number === 1" class="items-center">
                <div
                  class="flex flex-col justify-between text-center pt-8 px-[5.5rem]"
                >
                  <div>
                    <p class="font-[700] text-22 mb-4">
                      Welcome to your AI Assistant
                    </p>
                    <p class="font-normal text-16 mb-8">
                      Powered by generative artificial intelligence and natural
                      language processing, the AI Assistant serves as your
                      virtual companion, dedicated to assisting you throughout
                      your Cisco learning journey.
                    </p>
                  </div>
                </div>
                <div class="w-full text-center" />
                <GenAiPagination
                  class="p-6 px-10 pb-2"
                  :current-slide-number="current_slide_number"
                  :show-next="current_slide_number == 1"
                  :allow-next-nav="true"
                  :exitBtn="false"
                  :nextSlideText="'Next'"
                  @nextSlide="nextSlide"
                  @backSlide="prevSlide"
                  @hideGenAIModel="closeModal()"
                />
              </div>
              <div v-if="current_slide_number === 2" class="items-center">
                <div class="flex flex-wrap bg-[#F4F5F6] px-[7.1rem] py-16">
                  <div class="flex items-start mb-8 gap-4">
                    <img
                      class="h-10 w-10"
                      src="@/layout/GenAIBeta/assets/lock_filled.png"
                      alt="lock_filled_img"
                    />
                    <div>
                      <h2 class="font-[700] text-16 text-black mb-2">
                        The AI Assistant is secure and private by design
                      </h2>
                      <p class="text-14 text-black leading-[120%]">
                        Cisco collects only the data used to provide you with
                        the AI Assistant service, following a data minimization
                        approach. While the AI Assistant does retain past
                        conversations for you to easily reference in the future,
                        we take proactive measures to secure your data, adhering
                        to secure-by-design principles.
                      </p>
                    </div>
                  </div>
                  <div class="flex items-start gap-4">
                    <img
                      class="h-10 w-10"
                      src="@/layout/GenAIBeta/assets/frame_427320110.png"
                      alt="frame_427320110.png_img"
                    />
                    <div>
                      <h2 class="font-[700] text-16 text-black mb-2">
                        The AI Assistant strives for transparency
                      </h2>
                      <p class="text-14 text-black leading-[120%]">
                        Whenever possible, the AI Assistant provides sources for
                        the information used to answer your questions. For
                        additional information about the AI Assistant, check our
                        <cu-link
                          class="text-[#0076D5] underline"
                          :href="faqLink"
                          target="_blank"
                          rel="noopener"
                          >Cisco Learning AI Assistant FAQs.
                        </cu-link>
                      </p>
                    </div>
                  </div>
                </div>
                <div class="w-full text-center" />
                <div class="text-center mt-8">
                  <h3 class="font-[700] text-22 text-black">
                    The AI Assistant is committed to your privacy
                  </h3>
                </div>
                <GenAiPagination
                  class="p-8 pb-2"
                  :show-next="current_slide_number == 2"
                  :allow-next-nav="true"
                  :current-slide-number="current_slide_number"
                  :showPrevious="true"
                  :exitBtn="false"
                  @backSlide="prevSlide"
                  @nextSlide="nextSlide"
                />
              </div>
              <div v-if="current_slide_number === 3" class="items-center">
                <div class="flex flex-wrap bg-[#F4F5F6] px-[7.1rem] py-8">
                  <div class="flex items-start mb-8 gap-4">
                    <img
                      class="h-10 w-10"
                      src="@/layout/GenAIBeta/assets/social.png"
                      alt="social_img"
                    />
                    <div>
                      <h2 class="font-[700] text-16 text-black mb-2">
                        Be clear and use natural language
                      </h2>
                      <p class="text-14 text-black leading-[120%]">
                        Converse with the AI Assistant as you would with a
                        human. Be specific and provide details to help the
                        Assistant deliver more helpful responses.
                      </p>
                    </div>
                  </div>
                  <div class="flex items-start mb-8 gap-4">
                    <img
                      class="h-10 w-10"
                      src="@/layout/GenAIBeta/assets/socialDown.png"
                      alt="socialDown_img"
                    />
                    <div>
                      <h2 class="font-[700] text-16 text-black mb-2">
                        Provide feedback
                      </h2>
                      <p class="text-14 text-black leading-[120%]">
                        If the AI Assistant makes a mistake or you are not
                        satisfied with its response, use the built-in feedback
                        mechanisms to let us know. We will use your feedback to
                        improve the AI Assistant over time.
                      </p>
                    </div>
                  </div>
                  <div class="flex items-start gap-4">
                    <img
                      class="h-10 w-10"
                      src="@/layout/GenAIBeta/assets/interaction.png"
                      alt="interaction_img"
                    />
                    <div>
                      <h2 class="font-[700] text-16 text-black mb-2">
                        Verify information. You are in control.
                      </h2>
                      <p class="text-14 text-black leading-[120%]">
                        The AI Assistant is committed to providing accurate and
                        reliable information. It may not always get it right.
                        Please verify information before acting on it.
                      </p>
                    </div>
                  </div>
                </div>
                <div class="w-full text-center" />
                <div class="text-center mt-8">
                  <h3 class="font-[700] text-22 text-black">
                    A few tips for working with the AI Assistant
                  </h3>
                </div>
                <GenAiPagination
                  class="p-8 pb-2"
                  :show-next="current_slide_number == 3"
                  :allow-next-nav="true"
                  :current-slide-number="current_slide_number"
                  :showPrevious="true"
                  :exitBtn="false"
                  @backSlide="prevSlide"
                  @nextSlide="nextSlide"
                />
              </div>
              <div v-if="current_slide_number === 4" class="items-center">
                <div class="flex flex-wrap bg-[#F4F5F6] px-10 py-10">
                  <p class="font-normal text-16 text-black mb-4">
                    By continuing to use the AI Assistant, you are agreeing to
                    the following terms.
                  </p>
                  <div class="flex bg-white-lightest flex-col space-y-4 w-full">
                    <div
                      @click="clickPPA()"
                      @keyup.enter="clickPPA()"
                      @keyup.space="clickPPA()"
                      class="flex items-center justify-between border p-2 rounded-lg border-black-lightest cursor-pointer"
                    >
                      <div class="flex items-center space-x-2">
                        <div>
                          <h3 class="text-18 font-normal mb-1 underline">
                            AI Assistant terms & conditions
                          </h3>
                        </div>
                      </div>
                      <cu-icon-line-diagonal size="l" />
                    </div>
                  </div>
                </div>
                <div class="w-full text-center" />
                <div class="text-center mt-8">
                  <h3 class="font-[700] text-22 text-black">
                    Thank you for agreeing to the terms
                  </h3>
                </div>
                <GenAiPagination
                  class="p-8 pb-2"
                  :current-slide-number="current_slide_number"
                  :showPrevious="true"
                  :exitBtn="false"
                  :submitButton="true"
                  :hasAgree="hasClickedAgree"
                  @backSlide="prevSlide"
                  @handleSubmitButton="handleOptIn()"
                />
              </div>
            </slot>
          </div>
        </div>
      </div>
    </div>
  </cu-modal>
</template>
